// ── All Draws — every draw, opening on the ones that have not funded ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { localFilter, exportCSV, fmt, fmtDate } from "../../../shared/format";
import { Spinner, ErrorMsg, SectionHeader, SearchBar, Btn, Card, STh, Th, Td, TdNum, Icon, Pagination } from "../../../shared/ui";
import { useSortable, usePagination } from "../../../shared/hooks";
import { isDrawFunded } from "../../../shared/drawStatus";
import { useApp } from "../../../shared/appContext";

export const AllDrawsPage = () => {
        const { api, setModal, role, setConfirm, delRec } = useApp();
        const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true); const [err,setErr]=useState(null);
        const [q,setQ]=useState("");
        const [status,setStatus]=useState("unfunded");
        const load=()=>{setLoading(true);api.get("getDraws").then(d=>{setRows(d);setLoading(false);}).catch(e=>{setErr(e.message);setLoading(false);});};
        useEffect(()=>{load();},[]);
        const searched=localFilter(rows,q,["loannumber","companyname","drawrequestedbyName","drawfundedbyName"]);

        // OPENS ON THE UNFUNDED ONES. This page is where the dashboard's
        // Unfunded Draws tile lands, and an unfunded draw is the only kind
        // anyone has to do something about — but the funded ones are the
        // history you check against, so they stay one click away rather than
        // moving to a page of their own.
        //
        // The counts are taken AFTER the search box and before the status
        // filter, so they describe the rows a click would actually show you
        // rather than the whole table.
        const fundedCount   = searched.filter(isDrawFunded).length;
        const unfundedCount = searched.length - fundedCount;
        const filtered = status === "unfunded" ? searched.filter(d => !isDrawFunded(d))
                       : status === "funded"   ? searched.filter(isDrawFunded)
                       : searched;

        // Newest expected fund date first. Loan number put them in an order
        // that says nothing about which ones are imminent. The column is still
        // sortable; this only decides what you see before touching anything.
        const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'expectedfunddate', 'desc');
        const { paginated:displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);

        if(loading) return <Spinner />; if(err) return <ErrorMsg msg={err} onRetry={load} />;
        return <>
          <SectionHeader actions={[
            <SearchBar key="s" value={q} onChange={v=>{ setQ(v); reset(); }} placeholder="Search draws..." />,
            <Btn key="ex" onClick={()=>exportCSV(sorted,["loannumber","companyname","expectedfunddate","fundeddate","drawrequestedbyName","drawfundedbyName","expecteddrawamount","fundingamount"],"draws.csv")}>Export CSV</Btn>
          ]} />
          {/* The same pill row the closing-checklist page uses, so a filter
              looks like a filter everywhere in the app. The count rides on the
              label: a tab reading "Funded (0)" answers the question without
              being clicked. */}
          <Card style={{ marginBottom:12, padding:12 }}>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {[["unfunded","Unfunded",unfundedCount],
                ["funded","Funded",fundedCount],
                ["all","All",searched.length]].map(([key,label,count])=>(
                <button key={key} onClick={()=>{ setStatus(key); reset(); }} style={{
                  background: status===key ? B.cyan : "var(--color-background-secondary)",
                  border: "0.5px solid "+(status===key ? B.cyan : "var(--color-border-tertiary)"),
                  color: status===key ? "#fff" : "var(--color-text-secondary)",
                  borderRadius:6, padding:"6px 12px", cursor:"pointer", fontSize:13,
                  fontWeight: status===key ? 600 : 400
                }}>{label} <span style={{ opacity:0.8 }}>({count})</span></button>
              ))}
            </div>
          </Card>

          <Card style={{ overflowX:"auto" }}>
            <table style={{ width:"100%",borderCollapse:"collapse", whiteSpace:"nowrap" }}>
              <thead><tr>
                <STh label="Loan"          sortKey="loannumber"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Account"       sortKey="companyname"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Requested By"  sortKey="drawrequestedbyName" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Exp Fund Date" sortKey="expectedfunddate"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Exp Draw Amt"  sortKey="expecteddrawamount"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Funded By"     sortKey="drawfundedbyName"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Funded Date"   sortKey="fundeddate"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Funding Amt"   sortKey="fundingamount"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Same-Day Exempt" sortKey="samedayinterestexemption" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Capitalized"   sortKey="capitalized"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <Th></Th>
              </tr></thead>
              <tbody>{displayed.map(d=>(
                <tr key={d.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"draw-view",rec:{...d,id:d.id,loanid:d.loanid,expectedfunddate:d.expectedfunddate,fundingamount:d.fundingamount,fundeddate:d.fundeddate,expecteddrawamount:d.expecteddrawamount,drawfundedby:d.drawfundedby,drawrequestedby:d.drawrequestedby,samedayinterestexemption:d.samedayinterestexemption}})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                  <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{d.loannumber}</span></Td>
                  <Td style={{ fontWeight:500 }}>{d.companyname}</Td>
                  <Td>{d.drawrequestedbyName||"—"}</Td>
                  <Td>{fmtDate(d.expectedfunddate)}</Td>
                  <TdNum>{fmt(d.expecteddrawamount)}</TdNum>
                  <Td>{d.drawfundedbyName||"—"}</Td>
                  <Td>{fmtDate(d.fundeddate)}</Td>
                  <TdNum style={{ fontWeight:500 }}>{fmt(d.fundingamount)}</TdNum>
                  <Td style={{ textAlign:"center" }}>
                      {d.samedayinterestexemption ? <span style={{ color:"var(--color-cyan-dark)", fontWeight:600, fontSize:11 }}>Yes</span> : <span style={{ color:"var(--color-text-muted)", fontSize:11 }}>—</span>}
                    </Td>
                  <Td style={{ textAlign:"center" }}>
                      {d.capitalized ? <span style={{ color:"var(--color-cyan-dark)", fontWeight:600, fontSize:11 }}>Yes</span> : <span style={{ color:"var(--color-text-muted)", fontSize:11 }}>—</span>}
                    </Td>
                  <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                    <Btn onClick={()=>setModal({type:"draw-view",rec:{...d,id:d.id,loanid:d.loanid,expectedfunddate:d.expectedfunddate,fundingamount:d.fundingamount,fundeddate:d.fundeddate,expecteddrawamount:d.expecteddrawamount,drawfundedby:d.drawfundedby,drawrequestedby:d.drawrequestedby}})}><Icon type="view" /></Btn>
                    {role.canEdit&&<Btn onClick={()=>setModal({type:"draw-edit",rec:{...d,id:d.id,loanid:d.loanid,expectedfunddate:d.expectedfunddate,fundingamount:d.fundingamount,fundeddate:d.fundeddate,expecteddrawamount:d.expecteddrawamount,drawfundedby:d.drawfundedby,drawrequestedby:d.drawrequestedby},onDone:load})}><Icon type="edit" /></Btn>}
                    {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this draw?",onConfirm:()=>delRec("Draws",d.id,"Deleted draw",load)})} variant="danger"><Icon type="delete" /></Btn>}
                  </div></Td>
                </tr>
              ))}</tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />
          </Card>
        </>;
      };
